/**
 * v6.1.17.7 N.2 Team-UI — Inline team-name editor.
 *
 * Renders the team name with a pencil affordance on hover; click opens
 * an inline input + Save/Cancel.  Save → PATCH /leagues/{lid}/teams/{tid}/name
 * (see `backend/routes/identity.py::patch_team_name`).
 *
 * 429 handling: server returns `retry_after_seconds` in the response
 * body.  We render a countdown beneath the input ("Next rename in 4d 6h")
 * and disable Save until the lease expires.  Admin users are exempt
 * server-side, so they never see the countdown.
 *
 * Profanity is server-only — we don't bake a denylist client-side.
 */
import { useState } from 'react';
import { Pencil, Check, X } from 'lucide-react';
import { teamAPI } from '../lib/api';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { toast } from 'sonner';

const NAME_MIN = 2;
const NAME_MAX = 40;

function formatCountdown(seconds) {
  if (!seconds || seconds <= 0) return null;
  const d = Math.floor(seconds / 86400);
  const h = Math.floor((seconds % 86400) / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  if (d >= 1) return `${d}d ${h}h`;
  if (h >= 1) return `${h}h ${m}m`;
  return `${m}m`;
}

function validateName(name) {
  const trimmed = name.trim();
  if (trimmed.length < NAME_MIN) return `At least ${NAME_MIN} characters.`;
  if (trimmed.length > NAME_MAX) return `At most ${NAME_MAX} characters.`;
  return null;
}

export default function TeamNameInlineEditor({ team, onRenamed }) {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(team?.name || '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [rateLimitSeconds, setRateLimitSeconds] = useState(0);

  const openEdit = () => {
    setValue(team?.name || '');
    setError(null);
    setRateLimitSeconds(0);
    setEditing(true);
  };
  const closeEdit = () => {
    setEditing(false);
    setError(null);
    setRateLimitSeconds(0);
  };

  const clientError = editing ? validateName(value) : null;
  const canSubmit = editing && !saving && !clientError && rateLimitSeconds === 0 && value.trim() !== team?.name;

  const submit = async () => {
    if (!canSubmit) return;
    setSaving(true);
    setError(null);
    try {
      const res = await teamAPI.rename(team.league_id, team.id, value.trim());
      toast.success(`Team renamed to ${res.data.name}`);
      onRenamed?.(res.data);
      setEditing(false);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const status = e?.response?.status;
      const code = detail?.code;
      if (status === 429 && code === 'TEAM_RENAME_RATE_LIMITED') {
        setRateLimitSeconds(detail?.retry_after_seconds || 604800);
        setError(`Teams can be renamed once per week. ${formatCountdown(detail?.retry_after_seconds || 604800)} remaining.`);
      } else if (status === 400 && code === 'TEAM_NAME_REJECTED_MODERATION') {
        setError('This name was rejected. Please choose another.');
      } else if (status === 400 && code === 'TEAM_NAME_INVALID') {
        setError(detail?.message || 'Invalid team name.');
      } else if (status === 403 && code === 'TEAM_NOT_OWNED') {
        setError('Only the team owner can rename it.');
      } else {
        toast.error('Could not rename team — try again.');
      }
    } finally {
      setSaving(false);
    }
  };

  if (!team) return null;

  if (!editing) {
    return (
      <div className="flex items-center gap-2" data-testid="team-name-display">
        <h3 className="text-base font-bold">{team.name}</h3>
        {!team.frozen && (
        <button
          type="button"
          onClick={openEdit}
          className="p-1 rounded hover:bg-secondary/50 text-muted-foreground hover:text-foreground transition-colors"
          aria-label="Edit team name"
          title="Edit team name"
          data-testid="team-name-edit-btn"
        >
          <Pencil className="h-3.5 w-3.5" />
        </button>
        )}
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-1" data-testid="team-name-edit-mode">
      <div className="flex items-center gap-2">
        <Input
          value={value}
          onChange={(e) => { setValue(e.target.value); setError(null); setRateLimitSeconds(0); }}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && canSubmit) submit();
            if (e.key === 'Escape') closeEdit();
          }}
          maxLength={NAME_MAX}
          autoFocus
          disabled={saving}
          className="h-8 text-base font-bold"
          data-testid="team-name-edit-input"
        />
        <Button
          size="icon"
          variant="default"
          onClick={submit}
          disabled={!canSubmit}
          className="h-8 w-8"
          aria-label="Save team name"
          data-testid="team-name-save-btn"
        >
          <Check className="h-4 w-4" />
        </Button>
        <Button
          size="icon"
          variant="ghost"
          onClick={closeEdit}
          disabled={saving}
          className="h-8 w-8"
          aria-label="Cancel rename"
          data-testid="team-name-cancel-btn"
        >
          <X className="h-4 w-4" />
        </Button>
      </div>
      {clientError && (
        <p className="text-xs text-destructive" data-testid="team-name-client-error">{clientError}</p>
      )}
      {error && (
        <p className="text-xs text-destructive" data-testid="team-name-server-error">{error}</p>
      )}
      {rateLimitSeconds > 0 && (
        <p
          className="text-xs text-muted-foreground"
          data-testid="team-name-rate-limit-countdown"
        >
          Next rename available in {formatCountdown(rateLimitSeconds)}
        </p>
      )}
    </div>
  );
}

// Exported for unit testing.
export { formatCountdown, validateName };
